//Library Imports
import React, { useState, useEffect } from "react";
import store, { RootState } from "store";
import { useSelector } from "react-redux";

//Local Imports
import TableSort from "modules/Common/Components/TableSort";
import {
  resetAllTabsFilter,
  setSortDirection,
  resetAccountSearch,
  getConstantCampaignEmailId,
} from "modules/Analytics/utils";
import { getPaginatedAccountsData, setCampaignAndEmailIdsForDashboard } from "./actions";
import InfiniteLoader from "modules/Common/Components/InfiniteLoader";
import ContactListLoader from "../Contacts/ContactListLoader";
import { PATH_ANALYTICS, TAB_ANALYTICS } from "constants/paths";
import {
  ANALYTICS_ACCOUNTS_DASHBOARD,
  ANALYTICS_ACCOUNTS_DATA,
  DEFAULT_CAMPAIGN_IMAGE,
  ACCOUNTS_CONTENT_ENGAGEMENT_ANALYTICS,
  ANALYTICS_FILTERS,
} from "constants/types";
import Speed from "../../../assets/img/icons/Speed.svg";
import { getQueryCampaignId, getQueryEmailId, getQueryWorkflowId } from "modules/EmailBrowser/utils";
import { addCommaToNumber, getQueryParam, removeCampaignAndEmailQueryParams } from "utils";
import { analyticsDatesFilterChange, selectAsset } from "../Dashboard/actions";

//CSS Imports
import styled from "styled-components";
import { moveSelectedFiltersToApplied, resetRedirectUrls } from "../actions";
import { ANALYTICS_DEFAULT_DATES_FILTER } from "../Contacts/utils";
import { ANALYTICS_CAMPAIGNS_ID } from "constants/index";
import { Typography } from "antd";

import { moveAppliedFiltersToSelected } from "../actions";

const { Text } = Typography;

interface IAccountsList {
  searchedText: string;
  setSearchedText: (value: string) => void;
}
const AccountsList: React.FC<IAccountsList> = ({ searchedText, setSearchedText }) => {
  const searchReset = useSelector((state: RootState) => state.analyticsAccounts.searchReset);
  const accounts = useSelector((state: RootState) => state.analyticsAccounts.data);
  const loading = useSelector((state: RootState) => state.analyticsAccounts.loading);
  const loaded = useSelector((state: RootState) => state.analyticsAccounts.loaded);

  const [sortBy, setSortBy] = useState("");
  const [sortType, setSortType] = useState({ accessor: "", sortDirection: "none" });
  const [orderBy, setOrderBy] = useState("none");
  const [firstTime, setFirst] = useState(true);
  const selectedEmailIds = useSelector((state: RootState) => state.analyticsDashboard.selectedAssetsIds);
  const selectedCampaignIds = useSelector((state: RootState) => state.analyticsDashboard.selectedCampaignsIds);
  const selectedDatesObject = useSelector((state: RootState) => state.analyticsGraph.selectedDatesObject);
  const isFilterClicked = useSelector((state: RootState) => state.analyticsFilters.filterApplied);
  const AllCampaignsFilterCheck = useSelector(
    (state: RootState) => state.analyticsDashboard.campaignsFilters.isAllCampaigns
  );
  const filterReset = useSelector((state: RootState) => state.analyticsAccounts.filterReset);
  const { isShareProfile, isDailyIQ, isWeeklyAccountInsights } = useSelector(
    (state: RootState) => state.analyticsDashboard.appliedCampaignsFilters
  );
  const [ellipsis] = useState(true);

  const DEFAULT_PAGE_LIMIT = 20;

  const columnSortMap: any = {
    targeted_contacts: "0",
    total_sent: "1",
    total_opens: "2",
    unique_opens: "3",
    total_clicks: "4",
    unique_clicks: "5",
    campaign_score: "7",
  };

  const RedirectToAccountDetails = (row: any, history: any) => {
    moveAppliedFiltersToSelected();
    let pathname = `${history.location.pathname}${history.location.search}`;
    setCampaignAndEmailIdsForDashboard({
      campaignIds: selectedCampaignIds,
      emailIds: selectedEmailIds,
    });
    store.dispatch({ type: ANALYTICS_ACCOUNTS_DATA.SELECTED_ACCOUNT_RESET });
    store.dispatch({ type: ANALYTICS_ACCOUNTS_DASHBOARD.RESET, payload: false });
    let url = `/${PATH_ANALYTICS}/${TAB_ANALYTICS.ACCOUNTS}/dashboard?account_id=${row.companyId}`;
    let campaign_id = getQueryParam("campaign_id");
    let email_id = getQueryParam("email_id");
    let workflow_id = getQueryParam("workflow_id");
    if (campaign_id && email_id) url += `&campaign_id=${campaign_id}&email_id=${email_id}`;
    if (workflow_id) url += `&workflow_id=${workflow_id}`;
    store.dispatch({
      type: ANALYTICS_ACCOUNTS_DASHBOARD.SET_REDIRECT_URL,
      payload: { redirectUrl: pathname },
    });
    store.dispatch({
      type: ACCOUNTS_CONTENT_ENGAGEMENT_ANALYTICS.SET_REDIRECT_URL,
      payload: { redirectUrl: pathname },
    });
    history.push(url);
  };

  const getPagination = (resetPagination?: boolean, resetSearch?: boolean, sortParams?: any) => {
    const orderDataBy = sortParams?.orderBy ?? sortType.sortDirection;
    const orderBy = orderDataBy.length === 0 || orderDataBy === "none" ? "" : orderDataBy;
    let analyticsDashboard = store.getState().analyticsDashboard;
    let analyticsGraph = store.getState().analyticsGraph;
    let { selectedCampaignsIds, selectedAssetsIds, selectedWorkflowsIds, excludeAssetsIds, excludeCampaignsIds } = analyticsDashboard;
    let { isAllCampaigns } = analyticsDashboard.appliedCampaignsFilters;
    let { startDate, endDate } = analyticsGraph;

    let campaignIds;
    let emailIds;
    let workflowIds;

    if (filterReset === true || (resetSearch === true && firstTime === false)) {
      campaignIds = selectedCampaignsIds;
      emailIds = selectedAssetsIds;
      workflowIds = selectedWorkflowsIds;
    } else {
      if (selectedCampaignIds.length <= 0 && getQueryCampaignId() > 0 && firstTime === true) {
        campaignIds = selectedCampaignsIds.length > 0 ? selectedCampaignsIds : [getQueryCampaignId()];
        selectAsset(getQueryEmailId(), getQueryCampaignId());
        moveSelectedFiltersToApplied();
      } else campaignIds = selectedCampaignsIds;
      if (selectedAssetsIds.length <= 0 && getQueryEmailId() > 0 && firstTime === true) {
        emailIds = selectedAssetsIds.length > 0 ? selectedAssetsIds : [getQueryEmailId()];
      } else emailIds = selectedAssetsIds;
      if (selectedWorkflowsIds.length <= 0 && getQueryWorkflowId() > 0 && firstTime === true) {
        workflowIds = selectedWorkflowsIds.length > 0 ? selectedWorkflowsIds : [getQueryWorkflowId()];
      } else workflowIds = selectedWorkflowsIds;
    }

    if (startDate === "" && endDate === "") {
      analyticsDatesFilterChange(ANALYTICS_DEFAULT_DATES_FILTER);
      let analyticsGraph = store.getState().analyticsGraph;
      startDate = analyticsGraph.startDate;
      endDate = analyticsGraph.endDate;
    }
    // check if is share profile || daily IQ || weekly accounts insights
    if (isShareProfile || isDailyIQ || isWeeklyAccountInsights) {
      let campaign_email_id = getConstantCampaignEmailId({ isShareProfile, isDailyIQ, isWeeklyAccountInsights });
      campaignIds = [campaign_email_id];
      emailIds = [campaign_email_id];
    }

    return {
      allCampaigns: isAllCampaigns,
      searchWord: resetSearch === true ? "" : searchedText,
      limit: DEFAULT_PAGE_LIMIT,
      offset: resetPagination === true ? 0 : accounts.length,
      sortBy: orderBy === "" ? "" : columnSortMap[sortParams?.sortBy ?? sortBy] ?? "",
      orderBy: orderBy,
      campaignIds: campaignIds,
      emailIds: emailIds,
      workflowIds,
      startDate: startDate,
      endDate: endDate,
      excludeCampaignsIds,
      excludeAssetsIds,
    };
  };

  const sortedResults = (column?: any) => {
    let orderBy = "";
    if (column.canSort) {
      setSortBy(column.id);
      orderBy = setSortDirection(column.id, column.sortDirection);
      setOrderBy(orderBy);
      setSortType({ accessor: column.id, sortDirection: orderBy });
      getPaginatedAccountsData(getPagination(true, false, { sortBy: column.id, orderBy }));
    }
  };

  useEffect(() => {
    if (isFilterClicked === true) {
      store.dispatch({ type: ANALYTICS_ACCOUNTS_DATA.RESET });
      resetAllTabsFilter(true);
      store.dispatch({ type: ANALYTICS_FILTERS.RESET });
      getPaginatedAccountsData(getPagination(true, false));
    }
  }, [isFilterClicked]);

  useEffect(() => {
    if (!firstTime) {
      resetAccountSearch();
      getPaginatedAccountsData(getPagination(true, false));
    }
  }, [searchedText]);

  // useEffect(() => {
  //   if (
  //     selectedCampaignIds.length === 0 &&
  //     selectedEmailIds.length === 0 &&
  //     selectedDatesObject?.value === ANALYTICS_DEFAULT_DATES_FILTER.value &&
  //     firstTime === false
  //   ) {
  //     getPaginatedAccountsData(getPagination(true, false));
  //     removeCampaignAndEmailQueryParams();
  //   }
  // }, [
  //   firstTime,
  //   selectedCampaignIds,
  //   selectedEmailIds,
  //   selectedDatesObject,
  //   AllCampaignsFilterCheck,
  //   isShareProfile,
  //   isDailyIQ,
  //   isWeeklyAccountInsights,
  // ]);

  useEffect(() => {
    if (accounts.length <= 0 || searchReset === true || filterReset === true) {
      getPaginatedAccountsData(getPagination(true, true), setFirst);
      setFirst(false);
    } else setFirst(false);
    setSearchedText("");
  }, []);

  const columns = React.useMemo(
    () => [
      {
        Header: " ",
        disableSortBy: true,
        columns: [
          {
            Header: "Account Name",
            accessor: "companyName",
            disableSortBy: true,
            Cell: (props: any) => {
              let item = props.row.original;
              return (
                <div className="d-flex flex-row">
                  <div>
                    <img
                      className="company-logo"
                      src={
                        item.companyLogo !== null && item.companyLogo !== ""
                          ? item.companyLogo
                          : DEFAULT_CAMPAIGN_IMAGE
                      }
                      alt=""
                      onError={(e: any) => {
                        e.target.onerror = null;
                      }}
                    />
                  </div>
                  <div className="company-details d-flex flex-column">
                    <Text
                      className="company-name"
                      ellipsis={ellipsis ? { rows: 1, tooltip: item.companyName } : false}
                    >
                      {item.companyName}
                    </Text>
                    <Text
                      className="company-industry"
                      ellipsis={ellipsis ? { rows: 1, tooltip: item.industry } : false}
                    >
                      {item.industry}
                    </Text>
                  </div>
                </div>
              );
            },
          },
          {
            Header: "Contacts",
            id: "targeted_contacts",
            accessor: "targetedContacts",
            sortType: "basic",
            canSort: true,
            sortDirection: sortType.accessor === "targeted_contacts" ? sortType.sortDirection : "none",
            Cell: (props: any) => {
              let item = props.row.original;
              return addCommaToNumber(item.targetedContacts?.toString() ?? "");
            },
          },
          {
            Header: "Sent (Delivered % )",
            id: "total_sent",
            accessor: "totalSent",
            sortType: "basic",
            canSort: true,
            sortDirection: sortType.accessor === "total_sent" ? sortType.sortDirection : "none",
            Cell: (props: any) => {
              let item = props.row.original;
              return (
                <div className="total_sent d-flex align-items-center">
                  <span data-totalsent="totalsent">{addCommaToNumber(item.totalSent?.toString() ?? "")}</span>{" "}
                  <div>&nbsp;({item.sentPercentage}%)</div>
                </div>
              );
            },
          },
        ],
      },
      {
        Header: "Opens",
        disableSortBy: true,
        columns: [
          {
            Header: "Total",
            id: "total_opens",
            accessor: "totalOpens",
            sortType: "basic",
            canSort: true,
            sortDirection: sortType.accessor === "total_opens" ? sortType.sortDirection : "none",
            Cell: (props: any) => {
              let item = props.row.original;
              return addCommaToNumber(item.totalOpens?.toString() ?? "");
            },
          },
          {
            Header: "Unique",
            id: "unique_opens",
            accessor: "uniqueOpens",
            sortType: "basic",
            canSort: true,
            sortDirection: sortType.accessor === "unique_opens" ? sortType.sortDirection : "none",
            Cell: (props: any) => {
              let item = props.row.original;
              return addCommaToNumber(item.uniqueOpens?.toString() ?? "");
            },
          },
        ],
      },
      {
        Header: "Clicks",
        disableSortBy: true,
        columns: [
          {
            Header: "Total",
            id: "total_clicks",
            accessor: "totalClicks",
            sortType: "basic",
            canSort: true,
            sortDirection: sortType.accessor === "total_clicks" ? sortType.sortDirection : "none",
            Cell: (props: any) => {
              let item = props.row.original;
              return addCommaToNumber(item.totalClicks?.toString() ?? "");
            },
          },
          {
            Header: "Unique",
            id: "unique_clicks",
            accessor: "uniqueClicks",
            sortType: "basic",
            canSort: true,
            sortDirection: sortType.accessor === "unique_clicks" ? sortType.sortDirection : "none",
            Cell: (props: any) => {
              let item = props.row.original;
              return addCommaToNumber(item.uniqueClicks?.toString() ?? "");
            },
          },
        ],
      },
      {
        Header: "Score",
        disableSortBy: true,
        columns: [
          {
            Header: "Campaign(s)",
            id: "campaign_score",
            accessor: "campaignsScore",
            sortType: "basic",
            canSort: true,
            sortDirection: sortType.accessor === "campaign_score" ? sortType.sortDirection : "none",
            Cell: (props: any) => {
              let item = props.row.original;
              return (
                <div className="last-item">
                  <div className="engagement">
                    <img src={Speed} alt="" />
                    &nbsp;
                    {addCommaToNumber((item.campaignsScore ?? 0)?.toString() ?? "")}
                  </div>
                </div>
              );
            },
          },
          {
            Header: "Lifetime",
            id: "lifetime_score",
            accessor: "lifetimeScore",
            disableSortBy: true,
            Cell: (props: any) => {
              let item = props.row.original;
              return (
                <div className="last-item">
                  <div className="engagement">
                    <img src={Speed} alt="" />
                    &nbsp;
                    {addCommaToNumber((item.lifetimeScore ?? 0)?.toString() ?? "")}
                  </div>
                </div>
              );
            },
          },
        ],
      },
    ],
    [sortType]
  );

  return (
    <Wrapper>
      {/* <div id="accounts-list-table" className="scrollable-table "> */}
      {/* <InfiniteLoader
        dataLength={accounts?.length}
        next={() => getPaginatedAccountsData(getPagination(false, true))}
        hasMore={true}
        loader={<ContactListLoader loading={loading} />}
        // scrollableTarget={"accounts-list-table"}
        // scrollThreshold={100}
        scrollableTarget="scrollableDiv"
      > */}
      <TableSort
        columns={columns}
        data={accounts}
        module={"accounts"}
        _onCellClick={sortedResults}
        _onRowClick={RedirectToAccountDetails}
        next={() => getPaginatedAccountsData(getPagination(false, true))}
        hasMore={true}
        loader={[1, 2, 3].map((item) => (
          <ContactListLoader key={item} loading={loading} />
        ))}
      />
      {/* {[1, 2, 3].map((item) => (
          <ContactListLoader key={item} loading={loading} />
        ))} */}
      {loaded && accounts?.length === 0 ? <div className="no-data">No Data Found!</div> : null}
      {/* </InfiniteLoader> */}
      {/* </div> */}
    </Wrapper>
  );
};

export default AccountsList;

const Wrapper = styled.div`
  .no-data {
    width: 100%;
    height: 100px;
    display: flex;
    justify-content: center;
    align-items: center;
    font-family: "robotomedium";
    color: #67696a;
    font-size: 1rem;
    margin-bottom: 30px;
    /* box-shadow: 6px 30px 73px -10px grey; */
    border-radius: 5px;
  }
  .total_sent {
    color: #1ec252;
    font-weight: 500;
    font-size: 20px !important;
    font-family: "Roboto", sans-serif;
    width: 100%;
    justify-content: center;
    div {
      font-size: 16px !important;
    }
  }
  .company-logo {
    width: 55px;
    height: 55px;
    border-radius: 9px;
  }
  .engagement {
    border-radius: 12px;
    background-color: #1971eb;
    color: #ffffff;
    font-size: 16px;
    font-family: "robotomedium";
    font-weight: 500;
    padding: 0px 10px;
    display: inline;
    float: right;
    white-space: nowrap;
  }
  /* .scrollable-table {
    margin-bottom: 0px;
    position: relative;
    min-height: calc(100vh - 350px);
    max-height: calc(100vh - 350px);
    overflow: auto;
  } */
  table {
    width: 100%;
    margin-top: 10px;
    margin-bottom: 20px;
    thead {
      border-bottom: 1px solid #dddddd;
      tr {
        padding: 0 15px;
        &:before {
          max-height: 50px;
          @media all and (max-width: 1439px), (max-height: 729px), (-webkit-device-pixel-ratio: 1.25) {
            max-height: 38px;
          }
        }
      }
      th {
        color: #5e7079;
        font-weight: 700;
        font-size: 18px;
        font-family: "Roboto", sans-serif;
        white-space: nowrap;
        position: sticky;
        top: 0;
        z-index: 1;
        span {
          width: 15px;
          .sort_asc {
            width: 10px;
            height: 8px;
            margin-top: -15px;
            margin-left: 5px;
            float: left;
            @media all and (max-width: 1439px), (-webkit-device-pixel-ratio: 1.25) {
              margin-top: -13px;
            }
            img {
              transform: rotate(180deg);
            }
          }
          .sort_desc {
            width: 10px;
            height: 8px;
            float: left;
            margin-left: 5px;
            margin-top: 5px;
            img {
              float: left;
            }
          }
        }
      }
      tr:nth-child(1) {
        justify-content: end;
        display: flex;
        th {
          font-size: 18px;
          font-weight: 700;
          color: #5e7079;
          font-family: "Roboto", sans-serif;
          justify-content: center;
          white-space: nowrap;
          position: sticky;
          top: 0;
          z-index: 1;
          @media all and (max-width: 1439px), (-webkit-device-pixel-ratio: 1.25) {
            font-size: 14px;
          }

          &:nth-child(2) {
            width: 200px;
            @media all and (max-width: 1439px) {
              width: 120px;
            }
          }
          &:nth-child(3) {
            width: 200px;
            @media all and (max-width: 1439px) {
              width: 120px;
            }
          }
          &:nth-child(4) {
            width: 240px;
            @media all and (max-width: 1439px) {
              width: 200px;
            }
          }
        }
      }
      tr:nth-child(2) {
        th {
          font-size: 16px;
          color: #5e7079;
          font-weight: 500;
          font-family: "Roboto", sans-serif;
          white-space: nowrap;
          white-space: nowrap;
          position: sticky;
          top: 0;
          z-index: 1;
          @media all and (max-width: 1439px), (-webkit-device-pixel-ratio: 1.25) {
            font-size: 14px;
          }
          &:nth-child(3) {
            justify-content: center;
          }
        }
      }
    }
    tbody {
      width: 100%;
      display: block;
      box-sizing: border-box;
      overflow-y: auto;
      height: calc(100vh - 340px);
      /* padding-bottom: 90px; */
      @media all and (max-width: 1439px), (max-height: 729px), (-webkit-device-pixel-ratio: 1.25) {
        height: calc(100vh - 300px);
      }
      scrollbar-width: thin;
      scrollbar-color: #001760 #efefef;
      &::-webkit-scrollbar {
        width: 5px;
        height: 5px;
        border-radius: 5px;
      }
      /* Track */
      &::-webkit-scrollbar-track {
        background: #fff;
        margin-top: 0;
        margin-bottom: 90px;
        @media all and (max-width: 1439px), (max-height: 729px), (-webkit-device-pixel-ratio: 1.25) {
          margin-top: 0;
        }
      }
      /* Handle */
      &::-webkit-scrollbar-thumb {
        background: #001760;
      }
      /* Handle on hover */
      /* &::-webkit-scrollbar-thumb:hover {
        background: #efefef;
      } */
      > div {
        overflow: hidden;
      }
      tr {
        background-color: #ffffff;
        box-shadow: 0px 0px 17px -2px rgba(0, 0, 0, 0.08);
        width: 100%;
        border-radius: 8px;
        transition-duration: 0.2s;
        position: relative;
        margin: 4px 0;
        padding: 16px 15px 10px 15px;
        cursor: pointer;
      }
    }
    thead,
    tbody {
      width: 100%;
      display: block;
      float: left;
      tr:nth-child(1) {
        th {
          &:nth-child(1) {
            @media all and (max-width: 1439px) {
              //Safari (7.1+)
              _::-webkit-full-page-media,
              _:future,
              :root .safari_only {
                width: calc(100% - 450px) !important;
              }
              //Safari (from 6.1 to 10)
              @media screen and (min-color-index: 0) and(-webkit-min-device-pixel-ratio:0) {
                @media {
                  width: calc(100% - 450px) !important;
                }
              }
              //Safari (10.1+)
              @media not all and (min-resolution: 0.001dpcm) {
                @media {
                  width: calc(100% - 450px) !important;
                }
              }
            }
          }
        }
      }
      tr {
        width: 100%;
        display: block;
        float: left;
        td {
          min-height: 70px;
          font-family: "Roboto", sans-serif;
          font-weight: 500;
          font-size: 20px !important;
          @media all and (max-width: 1439px), (-webkit-device-pixel-ratio: 1.25) {
            font-size: 14px !important;
          }
        }
        th,
        td {
          display: block;
          float: left;
          &:nth-child(1) {
            width: calc(80% - 890px);
            text-align: left;
            @media all and (max-width: 1621px), (-webkit-device-pixel-ratio: 1.25) {
              width: calc(90% - 780px);
            }
            @media all and (max-width: 1439px) {
              width: calc(100% - 655px);
            }

            > div {
              display: flex;
              width: 100%;
              align-items: center;
              .company-logo {
                float: left;
                margin-right: 14px;
              }
              .company-details {
                float: left;
                font-weight: 400;
                color: #000000;
                font-size: 20px;
                font-family: "Roboto Slab", serif;
                white-space: nowrap;
                text-overflow: ellipsis;
                overflow: hidden;
                @media all and (max-width: 1439px), (-webkit-device-pixel-ratio: 1.25) {
                  font-size: 14px;
                }
                .company-name {
                  color: #000000;
                  font-weight: 500;
                  font-size: 20px;
                  font-family: "Roboto", sans-serif;
                  @media all and (max-width: 1439px), (-webkit-device-pixel-ratio: 1.25) {
                    font-size: 14px;
                  }
                }
                .company-industry {
                  color: #27ce0a;
                  font-weight: 500;
                  font-size: 14px;
                  font-family: "Roboto", sans-serif;
                  @media all and (max-width: 1439px), (-webkit-device-pixel-ratio: 1.25) {
                    font-size: 12px;
                  }
                }
              }
            }
          }
          &:nth-child(2) {
            width: 20%;
            text-align: left;
            display: inline-flex;
            align-items: center;
            justify-content: center;
            @media all and (max-width: 1621px), (-webkit-device-pixel-ratio: 1.25) {
              width: 10%;
            }
            @media all and (max-width: 1439px) {
              width: 75px;
            }
          }
          &:nth-child(3) {
            width: 250px;
            text-align: left;
            display: inline-flex;
            align-items: center;
            &[role="cell"] {
              color: #27ce0a;
            }
            @media all and (max-width: 1621px), (-webkit-device-pixel-ratio: 1.25) {
              width: 140px;
            }
            /* @media all and (max-width: 1439px) {
              width: 75px;
            } */
          }
          &:nth-child(4) {
            width: 100px;
            text-align: center;
            display: inline-flex;
            align-items: center;
            justify-content: center;

            @media all and (max-width: 1439px) {
              width: 60px;
            }
          }
          &:nth-child(5) {
            width: 100px;
            text-align: center;
            display: inline-flex;
            align-items: center;
            justify-content: center;
            @media all and (max-width: 1439px) {
              width: 60px;
            }
          }
          &:nth-child(6) {
            width: 100px;
            text-align: center;
            display: inline-flex;
            align-items: center;
            justify-content: center;
            @media all and (max-width: 1439px) {
              width: 60px;
            }
          }
          &:nth-child(7) {
            width: 100px;
            text-align: center;
            display: inline-flex;
            align-items: center;
            justify-content: center;
            @media all and (max-width: 1439px) {
              width: 60px;
            }
          }
          &:nth-child(8) {
            width: 120px;
            display: inline-flex;
            align-items: center;
            justify-content: center;
            @media all and (max-width: 1439px) {
              width: 100px;
            }
          }
          &:nth-child(9) {
            width: 120px;
            display: inline-flex;
            align-items: center;
            justify-content: center;
            @media all and (max-width: 1439px) {
              width: 100px;
            }
          }
        }
      }
    }
  }
`;
